// =============================================================================
// PLATFORM CRITERIA — the STRUCTURED, SOURCED, VALIDATED scoreable unit of the
// Get-Found optimizer (Plan C). The structured sibling of platform_algorithm_rules
// (which stays prose, feeding the engine PROMPT). One row = one checkable criterion
// (a char limit, a skills floor, a presence check), each carrying its own numeric
// value, its plain-English explanation, its citation(s), and — the heart of the
// no-invention law applied to the optimizer — its validation_status.
//
// THE GATE (rule-zero, in code not prompt): the resolver reads ONLY rows with
// validation_status='validated' AND NOT stale. A criterion that is not validated
// PHYSICALLY CANNOT enter a score. Everything starts 'flagged' (default), so nothing
// is scored until an Exa-backed validation pass confirms it with real sources —
// the optimizer-domain instance of "absent signal → null, never fabricated."
// (Jason 2026-06-23: validation is Exa-AUTOMATED with a shown confidence benchmark;
// no hardcoded FIELD_LIMITS, no guessed values — see docs/PLAN-platform-optimizer-C.md.)
//
// Cross-user / cross-platform (NO userId) like platform_algorithm_rules:7. Keyed by
// criterionKey so a criterion is stored once and re-research upserts (ON CONFLICT).
// Additive only (rule #11), applied via the Neon tool, mirrored here.
// =============================================================================

import { sql } from 'drizzle-orm';
import { index, integer, jsonb, pgTable, text, uniqueIndex, varchar } from 'drizzle-orm/pg-core';
import { id, lakeProvenance, timestamps, type LakeSource } from './_shared';

/**
 * The closed set of interpreter kinds the PURE scorer knows how to check. NEVER
 * eval'd from the DB — the DB stores the kind + params (data); the scorer keeps the
 * tiny pure assertion per kind (mechanics, in code). An unknown kind is skipped +
 * flagged (forward-compat), never crashes.
 */
export type CriterionCheckKind =
  | 'char_limit' // field length <= num_value (with num_min as the good-range floor)
  | 'char_min' // field length >= num_value
  | 'count_min' // list field length >= num_value (e.g. skills >= 6)
  | 'count_max' // list field length <= num_value
  | 'boolean_presence' // field present / leads-with-title (no number)
  | 'ratio_max'; // stuffing: max repeats of a term before it's penalised

/** The validation gate states — only 'validated' is ever scored. */
export type CriterionValidationStatus = 'validated' | 'flagged' | 'contested' | 'rejected';

/**
 * The 3-way readout role (the P1-resolution hybrid, Jason 2026-06-23 — see
 * docs/PLAN-platform-optimizer-C.md §10). A VALIDATED criterion still only enters the
 * SCORE when its role is 'scored'; the other two are shown but never scored, so an
 * un-checkable universal is surfaced as guidance, never as a fabricated "met":
 *  - 'scored'     — checkable on the generated profile → counts in the %.
 *  - 'guaranteed' — the system already enforces it (honesty via the grounding gate,
 *                   plain-text output) → shown ✓ "we handle this", contributes ZERO.
 *  - 'advisory'   — a strategy/behaviour not in the generated text (be active, place
 *                   keywords early) → shown as guidance, NEVER a fabricated met.
 */
export type CriterionDisplayRole = 'scored' | 'guaranteed' | 'advisory';

export const platformCriteria = pgTable(
  'platform_criteria',
  {
    id: id(),
    // Dedup slug, e.g. 'linkedin.headline.max_chars'. UNIQUE → research-once + upsert.
    criterionKey: varchar('criterion_key', { length: 200 }).notNull().unique(),
    // Same 11-bucket vocabulary as platform_algorithm_rules.platform (shared key space).
    platform: varchar('platform', { length: 40 }).notNull(),
    // Which input it checks: 'headline'|'about'|'skills'|'experience'|'profile'|… Null
    // for a non-field criterion. Extensible string (unfreezes the headline/about-only limit).
    field: varchar('field', { length: 40 }),
    // The enumerated interpreter kind (closed set in code, see CriterionCheckKind).
    checkKind: varchar('check_kind', { length: 40 }).notNull(),
    // The structured number (e.g. 220). Null = not researched (the no-invention null).
    numValue: integer('num_value'),
    // Optional band low (headline min 70) — splits "good range" from "hard cap".
    numMin: integer('num_min'),
    // 'chars'|'words'|'items'|'bool' — so scorer + FE render the right unit.
    unit: varchar('unit', { length: 16 }),
    // 'high'|'medium'|'low' leverage → feeds the scorer's importance weighting.
    importance: varchar('importance', { length: 8 }).notNull().default('medium'),
    // 'scored'|'guaranteed'|'advisory' (see CriterionDisplayRole) — HOW the criterion
    // participates in the readout. Only 'scored' enters the %; 'guaranteed'/'advisory'
    // are shown but never scored (an un-checkable universal is guidance, never a faked
    // met). DATA, not hardcoded — re-classifiable by a research pass. Default 'scored'.
    displayRole: varchar('display_role', { length: 16 }).notNull().default('scored'),
    // User-facing lever label (replaces the hardcoded label:).
    label: varchar('label', { length: 200 }).notNull(),
    // The EXPLICIT up-front explanation the FE shows without a tap (decision 5).
    plainEnglish: text('plain_english').notNull(),
    // The "machine behind the curtain" line — behind the detail tap. Now sourced.
    whyItMatters: text('why_it_matters'),
    // What to do when unmet (replaces hardcoded detail:). Templated ({n}) for the live number.
    fixHint: text('fix_hint'),
    // THE GATE: 'validated'|'flagged'|'contested'|'rejected'. Defaults 'flagged' so a
    // row is NEVER scored until explicitly validated with sources.
    validationStatus: varchar('validation_status', { length: 16 }).notNull().default('flagged'),
    // Provenance link to the prose rule it derives from (platform_algorithm_rules.rule_key).
    sourceRuleKey: varchar('source_rule_key', { length: 200 }),
    // Which pass touched it, e.g. 'criteria-research-2026-06-23' / 'exa-validate'.
    researchedBy: varchar('researched_by', { length: 64 }),
    // 0-100 confidence — Jason's "degree of certainty" benchmark, shown to the user.
    confidence: integer('confidence'),
    // 1 = sources disagree; surfaced, never silently resolved/averaged.
    contested: integer('contested').notNull().default(0),
    // For a contested criterion: the disagreeing values + their cites (never averaged).
    candidateValues: jsonb('candidate_values').$type<{ value: number; source: LakeSource }[]>(),
    // verify_state + sources[] + last_verified_at — the staleness/citation band every pond shares.
    ...lakeProvenance,
    ...timestamps,
  },
  (t) => ({
    keyIdx: index('platform_criteria_key_idx').on(t.criterionKey),
    platformKindIdx: index('platform_criteria_platform_kind_idx').on(t.platform, t.checkKind),
    // The validated-only filter is index-served.
    platformStatusIdx: index('platform_criteria_platform_status_idx').on(t.platform, t.validationStatus),
  }),
);

// =============================================================================
// PLATFORM CRITERIA GAPS — the VALIDATION QUEUE. Mirrors grounding_gaps exactly
// (idempotent capture, ranked surfacing) minus userId (cross-platform, like the
// lake itself). A criterion the scorer reached for but found unvalidated/missing is
// flagged here for the (Exa-automated) validation team — never guessed. The queue is
// demand-ranked: the criteria users actually hit float up by occurrences.
// =============================================================================
export const platformCriteriaGaps = pgTable(
  'platform_criteria_gaps',
  {
    id: id(),
    platform: varchar('platform', { length: 40 }).notNull(),
    // The slot needing validation (may not exist as a row yet).
    criterionKey: varchar('criterion_key', { length: 200 }).notNull(),
    field: varchar('field', { length: 40 }),
    // 'unsourced'|'missing_criterion'|'contested_sources'|'single_source'|'stale'|
    // 'source_dead'|'platform_uncovered'|'unknown_check_kind'.
    reason: varchar('reason', { length: 40 }).notNull(),
    // What to research, e.g. "Seek 'about' max — 1 source dated 2022, needs corroborator".
    detail: text('detail'),
    // Idempotent capture: the same gap re-seen bumps this instead of duplicating.
    occurrences: integer('occurrences').notNull().default(1),
    // 'open'|'researching'|'resolved'.
    status: varchar('status', { length: 16 }).notNull().default('open'),
    ...timestamps,
  },
  (t) => ({
    platformIdx: index('platform_criteria_gaps_platform_idx').on(t.platform, t.status),
    // Idempotent natural key — re-capturing the same gap upserts (bump occurrences).
    keyUq: uniqueIndex('platform_criteria_gaps_key_uq').on(t.platform, t.criterionKey, t.reason),
  }),
);

// =============================================================================
// PLATFORM CRITERIA VALIDATION RUNS — append-only coverage telemetry (mirrors
// grounding_runs). "N of 11 platforms validated" folds into /readiness the way
// grounding coverage does. Cross-platform (no userId).
// =============================================================================
export const platformCriteriaValidationRuns = pgTable(
  'platform_criteria_validation_runs',
  {
    id: id(),
    platform: varchar('platform', { length: 40 }).notNull(),
    // Criteria confirmed validated this pass.
    validated: integer('validated').notNull(),
    // Criteria still flagged (queued) this pass.
    flagged: integer('flagged').notNull(),
    // Which pass, e.g. 'exa-validate-2026-06-23'.
    runBy: varchar('run_by', { length: 64 }),
    ...timestamps,
  },
  (t) => ({
    platformIdx: index('platform_criteria_validation_runs_platform_idx').on(t.platform),
  }),
);
